"use client";

import { useContext, useEffect } from "react";
import dynamic from "next/dynamic";
import { HeroModule } from "@/components/HeroModule";
import { ThreeStepsModule } from "@/components/ThreeStepsModule";
import { CompaniesModule } from "@/components/CompaniesModule";
import { useWindowSize } from "@/hooks/browser/useWindowsSize";
import { useIntersectionObserver } from "@/hooks/observers/useIntersectionObserver";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { I_HomePage } from "@/data/types/pages";
import { GridModuleSkeleton } from "@/components/GridModule";
import { QREditorSkeletonWrapper } from "@/components/QREditorSkeleton";
import { SocialProof } from "@/components/SocialProof";
import { Banner } from "@/components/Banner";

const QREditorHomeModule = dynamic(
  () =>
    import("@/components/QREditorHomeModule").then((mod) => ({
      default: mod.QREditorHomeModule,
    })),
  {
    ssr: false,
    loading: () => <QREditorSkeletonWrapper />,
  }
);

const GridModule = dynamic(
  () =>
    import("@/components/GridModule/GridModule").then((mod) => ({
      default: mod.GridModule,
    })),
  { ssr: false, loading: () => <GridModuleSkeleton /> }
);

const AIModule = dynamic(() => import("@/components/AIModule").then(({ AIModule }) => AIModule), {
  ssr: false,
});

const AnalyticsBannerModule = dynamic(
  () =>
    import("@/components/AnalyticsBannerModule").then(
      ({ AnalyticsBannerModule }) => AnalyticsBannerModule
    ),
  { ssr: false }
);

const KeepItSimpleModule = dynamic(
  () =>
    import("@/components/KeepItSimpleModule").then(({ KeepItSimpleModule }) => KeepItSimpleModule),
  {
    ssr: false,
  }
);

const TabsModule = dynamic(
  () => import("@/components/TabsModule").then(({ TabsModule }) => TabsModule),
  {
    ssr: false,
  }
);

export const HomePageTemplate = ({
  data,
  amplitudeEvent,
}: {
  data: I_HomePage;
  amplitudeEvent?: string;
}) => {
  const screenSize = useWindowSize();
  const { setQrSelected } = useContext(AmplitudeContext);
  const { ref: gridModuleRef, isIntersecting: shouldLoadGridModule } =
    useIntersectionObserver<HTMLDivElement>({
      rootMargin: screenSize?.isTablet ? "-100px" : "200px",
      triggerOnce: true,
    });

  useEffect(() => {
    setQrSelected(undefined);
    amplitudeEvent && amplitudeTrackEvent(amplitudeEvent);
  }, []);

  return (
    <main>
      {data.qrEditorCompact && <QREditorHomeModule data={data.qrEditorCompact} />}

      {data.billboard && <HeroModule data={data.billboard} />}

      {data.socialProof && (
        <Banner
          light
          withoutPadding
          customBgColor={"neutral-white"}
        >
          <SocialProof
            socialProof={data.socialProof}
            className="hero-module__social-proof"
          />
        </Banner>
      )}

      <ThreeStepsModule data={data.featured2} />

      {data.companiesModule && <CompaniesModule data={data.companiesModule} />}

      <div ref={gridModuleRef}>
        {shouldLoadGridModule && (
          <GridModule
            data={data.previewHome}
            previewTranslations={data.previewTranslations}
            defaultPreviewTitle={data.previewHome.defaultPreviewTitle}
          />
        )}
      </div>

      {data.analyticsBanner && <AnalyticsBannerModule data={data.analyticsBanner} />}

      <KeepItSimpleModule data={data.banner} />

      <AIModule data={data.featuredAI} />

      <TabsModule
        data={{
          title: data.faqsHome.title,
          subtitle: data.faqsHome.subtitle,
          faqsHomeTabs: data.faqsHomeTabs,
        }}
        backgroundColor={data.faqsHome.backgroundColor}
        customClass={data.faqsHome.customClass}
        className="home-faqs-tabs"
      />
    </main>
  );
};
